// frontend/src/pages/VersionManagementPage.jsx
// Admin page for managing application version with rich text editor

import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import secureAxios from "../utils/secureAxios";
import { AuthContext } from "../contexts/AuthContext";
import RichTextEditor from "../components/common/RichTextEditor";
import "../assets/css/rich-text-editor.css";

const VersionManagementPage = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [versionInfo, setVersionInfo] = useState({
    current_version: "",
    updated_at: null,
    updated_by: null,
    version_history: [],
  });

  const [formData, setFormData] = useState({
    version: "",
    create_announcement: true,
    announcement_title: "",
    announcement_content: "",
    announcement_type: "release_notes",
  });

  useEffect(() => {
    fetchVersionInfo();
  }, []);

  const fetchVersionInfo = async () => {
    setLoading(true);
    setError(null);
    try {
      const versionRes = await secureAxios.get("/api/version");

      setVersionInfo(versionRes.data);
      
      // Set form to current version (user will edit it to new version)
      const currentVersion = versionRes.data.current_version || "1.0.0";
      setFormData(prev => ({
        ...prev,
        version: currentVersion,
        announcement_title: `Version ${currentVersion} Released`,
        announcement_content: `<p>WebVenly has been updated to version ${currentVersion}.</p><p><strong>What's new in this release:</strong></p><ul><li></li></ul>`,
      }));
    } catch (err) {
      console.error("Error fetching version info:", err);
      setError("Failed to load version information");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Auto-update announcement title when version changes
    if (name === "version") {
      setFormData(prev => ({
        ...prev,
        announcement_title: `Version ${value} Released`,
      }));
    }
  };

  const handleContentChange = (content) => {
    setFormData(prev => ({
      ...prev,
      announcement_content: content,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await secureAxios.put("/api/version", formData);
      
      setSuccess(`Version updated to ${response.data.version}${response.data.announcement_created ? ' and announcement created' : ''}`);
      
      // Refresh version info
      fetchVersionInfo();
    } catch (err) {
      console.error("Error updating version:", err);
      setError(err.response?.data?.error || "Failed to update version");
    } finally {
      setSaving(false);
    }
  };

  const announcementTypes = [
    { value: "release_notes", label: "Release Notes" },
    { value: "feature", label: "New Feature" },
    { value: "bugfix", label: "Bug Fix" },
    { value: "maintenance", label: "Maintenance" },
  ];

  // Check if user is primary tenant
  if (!user?.is_primary) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger">
          <i className="bi bi-shield-exclamation me-2"></i>
          Access denied. Only primary tenant administrators can manage the application version.
        </div>
        <Link to="/crm" className="btn btn-outline-secondary">
          <i className="bi bi-arrow-left me-1"></i>
          Back to Dashboard
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading version information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-gear me-2"></i>
            Version Management
          </h2>
          <p className="text-muted mb-0">
            Update the application version and create release announcements
          </p>
        </div>
        <Link to="/crm" className="btn btn-outline-secondary">
          <i className="bi bi-arrow-left me-1"></i>
          Back to Dashboard
        </Link>
      </div>

      <div className="row">
        {/* Current Version Card */}
        <div className="col-lg-4 mb-4">
          <div className="card h-100">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">
                <i className="bi bi-info-circle me-2"></i>
                Current Version
              </h5>
            </div>
            <div className="card-body">
              <div className="text-center py-3">
                <span className="display-4 text-primary fw-bold">
                  v{versionInfo.current_version}
                </span>
              </div>
              
              {versionInfo.updated_at && (
                <div className="text-muted small text-center">
                  <i className="bi bi-clock me-1"></i>
                  Updated: {new Date(versionInfo.updated_at).toLocaleString()}
                  {versionInfo.updated_by && (
                    <>
                      <br />
                      <i className="bi bi-person me-1"></i>
                      By: {versionInfo.updated_by}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Version History */}
          {versionInfo.version_history?.length > 0 && (
            <div className="card mt-3">
              <div className="card-header">
                <h6 className="mb-0">
                  <i className="bi bi-clock-history me-2"></i>
                  Version History
                </h6>
              </div>
              <ul className="list-group list-group-flush">
                {versionInfo.version_history.map((v, index) => (
                  <li key={v.version} className="list-group-item d-flex justify-content-between align-items-center">
                    <span>
                      {index === 0 && versionInfo.current_version === v.version ? (
                        <span className="badge bg-success me-2">Current</span>
                      ) : null}
                      v{v.version}
                    </span>
                    <small className="text-muted">
                      {new Date(v.release_date).toLocaleDateString()}
                    </small>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Update Version Form */}
        <div className="col-lg-8 mb-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="bi bi-arrow-up-circle me-2"></i>
                Release New Version
              </h5>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger alert-dismissible fade show">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                  <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                </div>
              )}

              {success && (
                <div className="alert alert-success alert-dismissible fade show">
                  <i className="bi bi-check-circle me-2"></i>
                  {success}
                  <button type="button" className="btn-close" onClick={() => setSuccess(null)}></button>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Version Number */}
                <div className="mb-3">
                  <label htmlFor="version" className="form-label">
                    New Version Number <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">v</span>
                    <input
                      type="text"
                      className="form-control"
                      id="version"
                      name="version"
                      value={formData.version}
                      onChange={handleChange}
                      placeholder="2026.01.01"
                      required
                    />
                  </div>
                  <small className="text-muted">
                    Format: YYYY.MM.DD (e.g., 2026.01.01) or semantic versioning (e.g., 1.2.3)
                  </small>
                </div>

                {/* Create Announcement Toggle */}
                <div className="mb-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="create_announcement"
                      name="create_announcement"
                      checked={formData.create_announcement}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="create_announcement">
                      <strong>Create release announcement</strong>
                      <small className="d-block text-muted">
                        Automatically notify users about this release
                      </small>
                    </label>
                  </div>
                </div>

                {/* Announcement Details (shown when toggle is on) */}
                {formData.create_announcement && (
                  <div className="border rounded p-3 mb-3 bg-light">
                    <h6 className="mb-3">
                      <i className="bi bi-megaphone me-2"></i>
                      Announcement Details
                    </h6>

                    {/* Announcement Type */}
                    <div className="mb-3">
                      <label htmlFor="announcement_type" className="form-label">
                        Announcement Type
                      </label>
                      <select
                        className="form-select"
                        id="announcement_type"
                        name="announcement_type"
                        value={formData.announcement_type}
                        onChange={handleChange}
                      >
                        {announcementTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Announcement Title */}
                    <div className="mb-3">
                      <label htmlFor="announcement_title" className="form-label">
                        Title
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="announcement_title"
                        name="announcement_title"
                        value={formData.announcement_title}
                        onChange={handleChange}
                        placeholder="Version X.Y.Z Released"
                      />
                    </div>

                    {/* Rich Text Content */}
                    <div className="mb-0">
                      <label className="form-label">
                        Content
                      </label>
                      <RichTextEditor
                        value={formData.announcement_content}
                        onChange={handleContentChange}
                        placeholder="Describe what's new in this release... You can paste screenshots directly!"
                        minHeight="250px"
                      />
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="d-flex justify-content-end gap-2">
                  <Link to="/crm/whats-new" className="btn btn-outline-secondary">
                    <i className="bi bi-eye me-1"></i>
                    View Release Notes
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving || !formData.version || formData.version === versionInfo.current_version}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                        Releasing...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-rocket-takeoff me-1"></i>
                        Release Version
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Help Card */}
          <div className="card mt-3">
            <div className="card-body">
              <h6>
                <i className="bi bi-lightbulb me-2 text-warning"></i>
                Tips for Version Management
              </h6>
              <ul className="mb-0 small text-muted">
                <li>Use date-based versioning (YYYY.MM.DD) for easy tracking</li>
                <li>Always create an announcement so users know what changed</li>
                <li><strong>Paste screenshots directly</strong> into the editor (Ctrl+V)</li>
                <li>Use formatting (bold, lists, headers) to organize content</li>
                <li>The frontend <code>version.js</code> file should also be updated to match</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionManagementPage;